import React, { useEffect, useMemo, useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { authed } from '../lib/api';
import { formatMoney } from '../lib/utils';
import { OrderStatusBadge, PaymentStatusBadge, isOverdueCollection, overdueDays, formatOverdueAge } from '../components/StatusBadges';
import { Link } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';

export default function Orders() {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        (async () => {
            try {
                const r = await authed('distributor', { method: 'GET', url: '/orders/mine' });
                setOrders(r.data);
            } catch (e) { console.error(e); }
            setLoading(false);
        })();
    }, []);

    // Overdue = distributor's own credit orders that have been dispatched or
    // delivered but still show payment_status = pending. We deliberately keep
    // this soft — a single banner + subtle per-card tint — so distributors
    // see it at a glance without the page feeling alarmist. The exact same
    // rule is used by the admin overdue highlight (see StatusBadges.js) so
    // "what the admin sees as overdue" matches "what the distributor is
    // nudged about" perfectly.
    const overdueOrders = useMemo(
        () => (orders || []).filter(isOverdueCollection),
        [orders]
    );
    const overdueTotal = useMemo(
        () => overdueOrders.reduce((sum, o) => sum + Number(o.total || 0), 0),
        [overdueOrders]
    );
    // Age of the oldest overdue order — surfaced in the banner so the
    // distributor sees the most pressing figure without opening each card.
    const oldestOverdueDays = useMemo(() => {
        let max = 0;
        for (const o of overdueOrders) {
            const d = overdueDays(o);
            if (typeof d === 'number' && d > max) max = d;
        }
        return max;
    }, [overdueOrders]);

    return (
        <div className="min-h-screen bg-sdf-ivory">
            <Header />
            <main className="max-w-3xl mx-auto px-4 py-6">
                <h1 className="text-2xl font-extrabold">Your Orders</h1>

                {/* Soft "Payment Due" nudge — appears only when at least one
                    credit order is dispatched/delivered but still pending. */}
                {!loading && overdueOrders.length > 0 && (
                    <div
                        role="status"
                        aria-live="polite"
                        className="mt-4 rounded-2xl border border-amber-300 bg-amber-50/80 px-4 py-3 flex items-start gap-3 shadow-sm"
                        data-testid="orders-overdue-banner"
                    >
                        <AlertTriangle size={18} className="text-amber-600 shrink-0 mt-0.5" aria-hidden="true" />
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-bold text-amber-900">
                                Payment Due
                                <span className="ml-2 text-[10px] font-bold uppercase tracking-widest text-amber-800/80 align-middle" data-testid="orders-overdue-count">
                                    {overdueOrders.length} {overdueOrders.length === 1 ? 'order' : 'orders'}
                                </span>
                                {oldestOverdueDays > 0 && (
                                    <span
                                        className="ml-2 inline-flex items-center px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-widest rounded-full bg-amber-200/70 text-amber-900 border border-amber-300 align-middle"
                                        data-testid="orders-overdue-oldest"
                                        title="Age of the oldest unpaid credit order"
                                    >
                                        Oldest {oldestOverdueDays}{oldestOverdueDays === 1 ? ' day' : ' days'}
                                    </span>
                                )}
                            </div>
                            <div className="text-xs text-amber-900/85 mt-0.5 leading-snug">
                                Your credit purchase{overdueOrders.length === 1 ? ' has been' : 's have been'} dispatched.
                                Please clear the outstanding amount of{' '}
                                <span className="font-extrabold text-amber-900" data-testid="orders-overdue-total">{formatMoney(overdueTotal)}</span>{' '}
                                to keep future orders flowing smoothly.
                            </div>
                        </div>
                    </div>
                )}

                {loading ? <div className="mt-6 flex justify-center"><div className="spinner"/></div> :
                    orders.length === 0 ? (
                        <div className="card-soft p-8 mt-4 text-center text-sdf-text-2">You haven&apos;t placed any orders yet.</div>
                    ) : (
                        <div className="mt-4 space-y-3">
                            {orders.map((o) => {
                                const overdue = isOverdueCollection(o);
                                const age = overdue ? formatOverdueAge(o) : null;
                                return (
                                    <Link
                                        to={`/orders/${o.id}`}
                                        key={o.id}
                                        // Subtle amber tint + left accent on overdue rows so the
                                        // banner and the specific offending orders visually match.
                                        // Keep hover state so the whole card still feels tappable.
                                        className={`card-soft p-4 block transition-colors ${
                                            overdue
                                                ? 'bg-amber-50/60 hover:bg-amber-100/70 border-l-4 border-l-amber-500'
                                                : 'hover:bg-neutral-50'
                                        }`}
                                        data-testid={`order-card-${o.id}`}
                                    >
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="min-w-0">
                                                <div className="font-bold flex items-center gap-1.5">
                                                    {overdue && (
                                                        <AlertTriangle
                                                            size={13}
                                                            className="text-amber-600 shrink-0"
                                                            aria-label="Payment due"
                                                            data-testid={`order-card-overdue-${o.id}`}
                                                        />
                                                    )}
                                                    <span>#{o.order_number}</span>
                                                </div>
                                                <div className="text-xs text-sdf-text-2 mt-0.5">{new Date(o.created_at).toLocaleString()}</div>
                                            </div>
                                            <div className="text-right shrink-0">
                                                <div className="font-extrabold">{formatMoney(o.total)}</div>
                                                <div className="text-[10px] font-bold uppercase tracking-widest text-sdf-text-2 mt-0.5">
                                                    {o.payment_method === 'credit' ? 'CREDIT' : 'COD'}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="mt-2 flex flex-wrap items-center gap-1.5">
                                            <OrderStatusBadge status={o.status} testid={`order-card-status-${o.id}`} />
                                            <PaymentStatusBadge status={o.payment_status} testid={`order-card-payment-${o.id}`} />
                                            {overdue && (
                                                <span
                                                    className="inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest rounded-full border bg-amber-100 text-amber-900 border-amber-300 whitespace-nowrap"
                                                    data-testid={`order-card-payment-due-${o.id}`}
                                                >
                                                    Payment Due{age ? ` · ${age}` : ''}
                                                </span>
                                            )}
                                        </div>
                                        <div className="mt-2 text-xs text-sdf-text-2 line-clamp-2">{o.items.map(i => `${i.flavour_name} × ${i.quantity}`).join(', ')}</div>
                                    </Link>
                                );
                            })}
                        </div>
                    )}
            </main>
            <Footer />
        </div>
    );
}
